// A recipe's "Qty per serve" is measured in the ingredient's own stock-out unit —
// the basis captured on the item (2026-05-20 hot-fix: stockOutBasis / stockOutUnit
// / stockOutQty). Typing a bare number without that context is ambiguous: "1" of a
// 560-gram Maggi pack could mean one pack or one gram.
//
// stockOutQty is set on almost no items today (2 of 158 in production), so the
// default falls back to 1 rather than leaving the field blank. Weight/Volume items
// carry a real unit (gram/ml); countable bases (Piece/Pack/Item) are their own unit;
// otherwise fall back to the item's unitOfMeasurement.

/** The subset of item fields this module reads. */
export interface StockOutSource {
  stockOutBasis?: string | null;
  stockOutUnit?: string | null;
  stockOutQty?: string | number | null;
  unitOfMeasurement?: string | null;
}

/** Unit a serve of this ingredient is counted in, e.g. "gram" / "piece". */
export function stockOutUnitLabel(item: StockOutSource | null | undefined): string {
  if (!item) return "";
  const unit = (item.stockOutUnit ?? "").trim();
  if (unit) return unit.toLowerCase();
  const basis = (item.stockOutBasis ?? "").trim();
  // "Item" is a generic basis, not a unit the operator would recognise on a
  // quantity field — fall through to the item's own unit of measurement.
  if (basis && basis.toLowerCase() !== "item") return basis.toLowerCase();
  return (item.unitOfMeasurement ?? "").trim().toLowerCase();
}

/**
 * Qty to pre-fill when this ingredient is picked. Uses the item's defined
 * stock-out quantity when present; otherwise 1, which is the sane serve for a
 * countable basis and a visible starting point for a weight/volume one.
 */
export function defaultQtyPerServe(item: StockOutSource | null | undefined): number {
  const raw = item?.stockOutQty;
  const n = raw == null || raw === "" ? NaN : Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/** Short hint shown under the qty field, e.g. "Stock-out basis: Weight · gram". */
export function stockOutHint(item: StockOutSource | null | undefined): string {
  if (!item) return "";
  const basis = (item.stockOutBasis ?? "").trim();
  const unit = stockOutUnitLabel(item);
  if (!basis && !unit) return "";
  const defined = item.stockOutQty != null && item.stockOutQty !== "";
  const parts = [basis || null, unit && unit !== basis.toLowerCase() ? unit : null]
    .filter(Boolean)
    .join(" · ");
  return defined
    ? `Stock-out basis: ${parts} — pre-filled from the item.`
    : `Stock-out basis: ${parts}. No per-serve qty set on the item, defaulting to 1.`;
}
